import { z } from 'zod';
import { cardCompareSchema } from './compare';

export const REPORT_LOCALES = ['fr', 'en'] as const;
export const REPORT_BUSINESS_UNITS = [
    'atlantic',
    'eastern_canada',
    'ontario',
    'western_canada',
] as const;
export const REPORT_PROVINCES = [
    'AB',
    'BC',
    'MB',
    'NB',
    'NL',
    'NS',
    'NT',
    'NU',
    'ON',
    'PE',
    'QC',
    'SK',
    'YT',
] as const;
export const REPORT_TOTAL_REGION = 'total';
export const REPORT_OMISSION_CAUSES = [
    'degraded',
    'failed',
    'metric_absent',
    'kpi_absent',
] as const;
export const REPORT_LIMITS = {
    sections: 8,
    windowsPerSection: 6,
    rowsPerSection: 40,
    envelopeCalls: 200,
} as const;

const isoDate = z.iso.date();
const text = z.string().trim().min(1).max(120);

export const reportRegionSchema = z.union([
    z.literal(REPORT_TOTAL_REGION),
    z.enum(REPORT_BUSINESS_UNITS),
    z.enum(REPORT_PROVINCES),
]);

const dateRange = { label: text, startDate: isoDate, endDate: isoDate };

function inOrder(range: { startDate: string; endDate: string }) {
    return range.startDate <= range.endDate;
}

const IN_ORDER = { message: 'startDate must not be after endDate' };

export const reportCompareRangeSchema = z
    .object(dateRange)
    .refine(inOrder, IN_ORDER);

export const reportWindowSchema = z
    .object({ ...dateRange, compare: reportCompareRangeSchema.optional() })
    .refine(inOrder, IN_ORDER);

export const reportRowSchema = z.object({
    kpi: z.string().min(1),
    metric: z.string().min(1),
    label: text.optional(),
    group: text.optional(),
    extras: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
        .optional(),
    bucket: z
        .object({ axis: z.string().min(1), key: z.string().min(1) })
        .optional(),
});

export const reportSectionDefinitionSchema = z.object({
    title: text,
    windows: z
        .array(reportWindowSchema)
        .min(1)
        .max(REPORT_LIMITS.windowsPerSection),
    regions: z
        .array(reportRegionSchema)
        .min(1)
        .refine((regions) => new Set(regions).size === regions.length, {
            message: 'regions must be unique',
        }),
    rows: z.array(reportRowSchema).min(1).max(REPORT_LIMITS.rowsPerSection),
});

export const reportDefinitionSchema = z.object({
    title: text,
    subtitle: text.optional(),
    reportDate: isoDate.optional(),
    locale: z.enum(REPORT_LOCALES),
    sections: z
        .array(reportSectionDefinitionSchema)
        .min(1)
        .max(REPORT_LIMITS.sections),
});

export const reportCellSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('scalar'),
        value: z.number(),
        compare: cardCompareSchema.optional(),
    }),
    z.object({
        type: z.literal('ratio'),
        rate: z.number().nullable(),
        numerator: z.number(),
        denominator: z.number(),
        compare: cardCompareSchema.optional(),
    }),
]);

const windowResult = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    startDate: isoDate,
    endDate: isoDate,
});

export const reportRowResultSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('heading'), label: z.string().min(1) }),
    z.object({
        kind: z.literal('data'),
        label: z.string().min(1),
        kpi: z.string().min(1),
        metric: z.string().min(1),
        unit: z.enum([
            'count',
            'currency',
            'percentage',
            'duration_seconds',
            'ratio',
        ]),
        regionGrain: z.enum(['national', 'brand', 'businessUnit', 'province']),
        group: z.string().min(1).optional(),
        cells: z.record(z.string(), z.record(z.string(), reportCellSchema)),
        notes: z.array(z.string().min(1)).optional(),
    }),
]);

export const reportSectionResultSchema = z.object({
    title: z.string().min(1),
    windows: z.array(windowResult.extend({ compare: windowResult.optional() })),
    regions: z.array(reportRegionSchema),
    rows: z.array(reportRowResultSchema),
});

export const reportOmissionSchema = z.object({
    section: z.number().int().min(0),
    row: z.number().int().min(0),
    window: z.string().min(1),
    region: reportRegionSchema,
    cause: z.enum(REPORT_OMISSION_CAUSES),
    reason: z.string().min(1).optional(),
});

export const reportResultSchema = z.object({
    title: z.string().min(1),
    subtitle: z.string().min(1).optional(),
    reportDate: isoDate.optional(),
    locale: z.enum(REPORT_LOCALES),
    generatedAt: z.iso.datetime(),
    dataAsOf: z.iso.datetime().nullable(),
    sections: z.array(reportSectionResultSchema),
    omissions: z.array(reportOmissionSchema),
    warnings: z.array(z.string().min(1)).optional(),
});

export const reportCardSchema = z.object({
    shape: z.literal('report'),
    reportId: z.string().min(1),
    title: z.string().min(1),
    locale: z.enum(REPORT_LOCALES),
    sectionCount: z.number().int().min(1),
    figureCount: z.number().int().min(0),
    omissionCount: z.number().int().min(0),
    dataAsOf: z.iso.datetime().nullable(),
    reportDate: isoDate.optional(),
});

export type ReportLocale = (typeof REPORT_LOCALES)[number];
export type ReportRegion = z.infer<typeof reportRegionSchema>;
export type ReportDefinition = z.infer<typeof reportDefinitionSchema>;
export type ReportCell = z.infer<typeof reportCellSchema>;
export type ReportRowResult = z.infer<typeof reportRowResultSchema>;
export type ReportSectionResult = z.infer<typeof reportSectionResultSchema>;
export type ReportOmission = z.infer<typeof reportOmissionSchema>;
export type ReportResult = z.infer<typeof reportResultSchema>;
